import styled, { x } from '@xstyled/styled-components';
import React, { FC } from 'react';
import LockIcon from '~/assets/icons/lock.svg';
import { Capsule } from '~/ui/capsules';
import { Flex } from '~/ui/flex/Flex';
import { Chapter, Sequence, TrainingState, useTrainingStore } from '../state';
import { Chevron } from './Chevron';
import { ItemContainer } from './ItemContainer';
import { SequenceIcon } from './SequenceIcon';
import { t } from 'ttag';
import { select } from '~/lib/select';

const selector = (s: TrainingState) =>
  select(s, 'highestUnlockedPosition', 'skip');

/** Title text inside the chapter accordion button (heading wraps the button). */
const ChapterTitleText = styled.spanBox`
  margin: 0;
  font: inherit;
  font-weight: inherit;
`;

/**
 * Shown under a chapter the learner tested out of in the pretest. Raleway only
 * loads its regular weight, so the label uses ralewayBold.
 */
const TestedOutLabel = styled.smallBox`
  font-family: ralewayBold;
  color: var(--color-green-dk);
`;

export const NavItem: FC<{
  expanded: boolean;
  item: Chapter | Sequence;
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  activeItemPosition: any;
  onClick: () => void;
  /** Sequences panel id for chapter aria-controls (CAT-1293). */
  panelId?: string;
  /** In a chapter the learner tested out of: done, and open for review. */
  testedOut?: boolean;
}> = ({
  item,
  onClick,
  expanded,
  activeItemPosition,
  panelId,
  testedOut = false
}) => {
  let { highestUnlockedPosition } = useTrainingStore(selector);
  const { skip } = useTrainingStore(selector);
  const { title, position } = item;

  if (highestUnlockedPosition < 0 && activeItemPosition) {
    highestUnlockedPosition = activeItemPosition;
  }

  const unlocked = testedOut || position <= highestUnlockedPosition;

  return (
    <ItemContainer
      ariaExpanded={expanded}
      item={item}
      onClick={onClick}
      panelId={panelId}
      testedOut={testedOut}
    >
      <Capsule highlighted={unlocked}>
        {item.kind === 'chapter' ? (
          <x.small fontWeight="bold" fontSize="16px">
            {position / 10000}
          </x.small>
        ) : (
          <SequenceIcon icon={item.icon} />
        )}
      </Capsule>
      <Flex flexDirection="column">
        {item.kind === 'chapter' ? (
          <>
            <ChapterTitleText
              fontSize={{ _: 'md', sm: 'xs' }}
              className="itemStyleForDarkTheme"
              mt="5px"
            >
              {title}
            </ChapterTitleText>
            {testedOut && (
              <TestedOutLabel fontSize={{ _: 'sm', sm: 'xs' }}>
                {t`Tested out`}
              </TestedOutLabel>
            )}
          </>
        ) : (
          <x.small
            fontSize={{ _: 'md', sm: 'xs' }}
            className="itemStyleForDarkTheme"
            mt="5px"
          >
            {title}
          </x.small>
        )}
      </Flex>
      <x.div
        marginTop="6px"
        w={24}
        h={24}
        display="flex"
        justifyContent="flex-end"
      >
        {unlocked ? (
          <Chevron expanded={expanded} />
        ) : !skip ? (
          <LockIcon
            role="img"
            aria-label={t`Finish previous chapter to unlock`}
          />
        ) : (
          false
        )}
      </x.div>
    </ItemContainer>
  );
};
